import { Layout } from "@/components/Layout";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, ArrowRight, Mail, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { logError } from "@/lib/log";
import { EnquiryDialog } from "@/components/EnquiryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;
        
        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(data);
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (attempts < maxAttempts) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        logError("Error checking payment status:", error);
        if (attempts < maxAttempts) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    if (status === "checking" || (status === "checking" && attempts < maxAttempts)) {
      checkPaymentStatus();
    }
  }, [sessionId, attempts, status]);

  return (
    <Layout>
      <section className="py-24 md:py-32 bg-[#F9FAF9] min-h-screen">
        <div className="container-clean">
          <div className="max-w-2xl mx-auto">
            {status === "checking" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <Loader2 className="w-16 h-16 text-[#7CB342] animate-spin mx-auto mb-6" />
                <h1 className="text-3xl font-bold text-[#1A2E12] mb-4">
                  Confirming Your Payment
                </h1>
                <p className="text-[#5C6B57]">
                  Please wait while we verify your payment...
                </p>
              </motion.div>
            )}

            {status === "success" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural overflow-hidden">
                  <div className="bg-[#2D5016] p-8 text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                    >
                      <CheckCircle2 className="w-20 h-20 text-white mx-auto mb-4" strokeWidth={1.5} />
                    </motion.div>
                    <h1 className="text-3xl font-bold text-white mb-2" data-testid="success-title">
                      Booking Confirmed!
                    </h1>
                    <p className="text-white/80">
                      Thank you for choosing Clean Kin
                    </p>
                  </div>
                  
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      <div className="bg-[#E8F5E9] rounded-xl p-6">
                        <h3 className="font-semibold text-[#1A2E12] mb-4">What's Next?</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#7CB342] mt-0.5" strokeWidth={1.5} />
                            <span className="text-[#5C6B57]">
                              You'll receive a confirmation email with your booking details
                            </span>
                          </li>
                          <li className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#7CB342] mt-0.5" strokeWidth={1.5} />
                            <span className="text-[#5C6B57]">
                              Our team will contact you 24 hours before your scheduled clean
                            </span>
                          </li>
                          <li className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#7CB342] mt-0.5" strokeWidth={1.5} />
                            <span className="text-[#5C6B57]">
                              We use only eco-friendly products for a healthier clean
                            </span>
                          </li>
                        </ul>
                      </div>

                      {paymentData && (
                        <div className="bg-[#F9FAF9] rounded-xl p-6">
                          <h3 className="font-semibold text-[#1A2E12] mb-3">Payment Details</h3>
                          <div className="flex justify-between items-center">
                            <span className="text-[#5C6B57]">Amount Paid</span>
                            <span className="text-2xl font-bold text-[#2D5016]">
                              £{(paymentData.amount_total / 100).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-4">
                        <Link to="/" className="flex-1">
                          <Button 
                            className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6 btn-primary"
                            data-testid="btn-home"
                          >
                            Back to Home
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                        <EnquiryDialog source="booking-success" subject="Post-booking question">
                          <Button
                            variant="outline"
                            className="w-full border-[#2D5016] text-[#2D5016] rounded-full py-6"
                            data-testid="booking-success-email"
                          >
                            <Mail className="mr-2 w-4 h-4" />
                            Email Us
                          </Button>
                        </EnquiryDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {(status === "error" || status === "expired") && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                    <span className="text-red-600 text-2xl">!</span>
                  </div>
                  <h1 className="text-2xl font-bold text-[#1A2E12] mb-4">
                    {status === "expired" ? "Payment Session Expired" : "Something Went Wrong"}
                  </h1>
                  <p className="text-[#5C6B57] mb-8">
                    {status === "expired" 
                      ? "Your payment session has expired. Please try booking again."
                      : "We couldn't verify your payment. Please contact us if you believe this is an error."
                    }
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/booking">
                      <Button className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 py-6">
                        Try Again
                      </Button>
                    </Link>
                    <EnquiryDialog source="booking-error" subject="Payment / booking support">
                      <Button variant="outline" className="border-[#2D5016] text-[#2D5016] rounded-full px-8 py-6" data-testid="booking-error-email">
                        <Mail className="mr-2 w-4 h-4" />
                        Contact Support
                      </Button>
                    </EnquiryDialog>
                  </div>
                </Card>
              </motion.div>
            )}

            {status === "pending" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
                    <span className="text-yellow-600 text-2xl">⏳</span>
                  </div>
                  <h1 className="text-2xl font-bold text-[#1A2E12] mb-4">
                    Payment Processing
                  </h1>
                  <p className="text-[#5C6B57] mb-8">
                    Your payment is still being processed. Please check your email for confirmation 
                    or contact us if you have any questions.
                  </p>
                  <EnquiryDialog source="booking-pending" subject="Payment processing question">
                    <Button className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 py-6" data-testid="booking-pending-email">
                      <Mail className="mr-2 w-4 h-4" />
                      Contact Us
                    </Button>
                  </EnquiryDialog>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BookingSuccess;
